import { DiffMatchPatch, DiffOp } from 'diff-match-patch-ts';

export type WordDiffOp = 'same' | 'add' | 'del';

export interface WordDiffSegment {
  op: WordDiffOp;
  text: string;
}

/** One line of After, or a line of Before that no After line replaced. */
export type ChangeRow =
  | { kind: 'same'; text: string }
  | { kind: 'edited'; text: string; segments: WordDiffSegment[] }
  | { kind: 'added'; text: string }
  | { kind: 'removed'; text: string };

const dmp = new DiffMatchPatch();
const WORD_TOKEN = /\s+|[\p{L}\p{N}_]+|[^\s\p{L}\p{N}_]+/gu;
// Highest code unit below the surrogate range, so every token encodes to one unit.
const MAX_TOKEN_ID = 0xd7ff;
// Dice similarity a changed line needs to be shown as an edit rather than a
// replacement (MediaWiki wikidiff2 `changeThreshold`, diff2html `matchWordsThreshold`).
const EDITED_MIN_SIMILARITY = 0.5;
// Most line-to-line comparisons one diff may run, across all its changed blocks
// (diff2html `matchingMaxComparisons`). Blocks past the budget stay unpaired.
const MAX_PAIRINGS = 2_500;

const OPS: Record<DiffOp, WordDiffOp> = {
  [DiffOp.Delete]: 'del',
  [DiffOp.Equal]: 'same',
  [DiffOp.Insert]: 'add',
};

interface TokenRun {
  op: WordDiffOp;
  tokens: string[];
}

/**
 * diff-match-patch over token arrays: each distinct token becomes one code
 * unit, its line-mode trick (as in `computeWordDiffs`). Null when there are
 * more distinct tokens than code units.
 */
function diffTokens(
  before: string[],
  after: string[],
  cleanup: boolean
): TokenRun[] | null {
  const ids = new Map<string, number>();
  const vocabulary: string[] = [];
  const encode = (tokens: string[]): string | null => {
    let encoded = '';
    for (const token of tokens) {
      let id = ids.get(token);
      if (id === undefined) {
        id = vocabulary.length + 1;
        if (id > MAX_TOKEN_ID) return null;
        ids.set(token, id);
        vocabulary.push(token);
      }
      encoded += String.fromCharCode(id);
    }
    return encoded;
  };
  const encodedBefore = encode(before);
  const encodedAfter = encode(after);
  if (encodedBefore === null || encodedAfter === null) return null;
  const diffs = dmp.diff_main(encodedBefore, encodedAfter, false);
  if (cleanup) dmp.diff_cleanupSemantic(diffs);
  return diffs.map(([op, encoded]) => ({
    op: OPS[op],
    tokens: Array.from(encoded, (unit) => vocabulary[unit.charCodeAt(0) - 1]!),
  }));
}

function mergeSegments(segments: WordDiffSegment[]): WordDiffSegment[] {
  const merged: WordDiffSegment[] = [];
  for (const segment of segments) {
    if (!segment.text) continue;
    const last = merged[merged.length - 1];
    if (last && last.op === segment.op) {
      last.text += segment.text;
    } else {
      merged.push({ ...segment });
    }
  }
  return merged;
}

/** Word diff; semantic cleanup folds shared filler words into the edits around them. */
export function diffWords(before: string, after: string): WordDiffSegment[] {
  const runs = diffTokens(
    before.match(WORD_TOKEN) ?? [],
    after.match(WORD_TOKEN) ?? [],
    true
  );
  if (!runs) {
    return mergeSegments([
      { op: 'del', text: before },
      { op: 'add', text: after },
    ]);
  }
  return mergeSegments(runs.map(({ op, tokens }) => ({ op, text: tokens.join('') })));
}

const visibleLength = (text: string): number => text.replace(/\s+/g, '').length;

export const hasText = (text: string): boolean => visibleLength(text) > 0;

function similarity(segments: WordDiffSegment[], before: string, after: string): number {
  const total = visibleLength(before) + visibleLength(after);
  if (!total) return 0;
  const shared = segments.reduce(
    (sum, segment) => (segment.op === 'same' ? sum + visibleLength(segment.text) : sum),
    0
  );
  return (2 * shared) / total;
}

const unpaired = (removed: string[], added: string[]): ChangeRow[] => [
  ...removed.map((text): ChangeRow => ({ kind: 'removed', text })),
  ...added.map((text): ChangeRow => ({ kind: 'added', text })),
];

/** Pairs each added line, in order, with its most similar removed line. */
function pairLines(removed: string[], added: string[]): ChangeRow[] {
  const rows: ChangeRow[] = [];
  let next = 0;
  for (const text of added) {
    let best: { index: number; segments: WordDiffSegment[]; score: number } | undefined;
    for (let index = next; index < removed.length; index += 1) {
      const segments = diffWords(removed[index]!, text);
      const score = similarity(segments, removed[index]!, text);
      if (!best || score > best.score) best = { index, segments, score };
    }
    if (best && best.score >= EDITED_MIN_SIMILARITY) {
      for (; next < best.index; next += 1) {
        rows.push({ kind: 'removed', text: removed[next]! });
      }
      rows.push({ kind: 'edited', text, segments: best.segments });
      next = best.index + 1;
    } else {
      rows.push({ kind: 'added', text });
    }
  }
  for (; next < removed.length; next += 1) {
    rows.push({ kind: 'removed', text: removed[next]! });
  }
  return rows;
}

/**
 * Lines first, then words inside similar line pairs, so a small edit to a long
 * prompt marks only what changed and a rewrite reads as whole lines.
 */
export function diffLines(before: string, after: string): ChangeRow[] {
  const beforeLines = before.split('\n');
  const afterLines = after.split('\n');
  const runs = diffTokens(beforeLines, afterLines, false) ?? [
    { op: 'del', tokens: beforeLines },
    { op: 'add', tokens: afterLines },
  ];
  const rows: ChangeRow[] = [];
  let removed: string[] = [];
  let added: string[] = [];
  let pairingsLeft = MAX_PAIRINGS;
  const flush = () => {
    const pairings = removed.length * added.length;
    const canPair = pairings <= pairingsLeft;
    if (canPair) pairingsLeft -= pairings;
    for (const row of canPair ? pairLines(removed, added) : unpaired(removed, added)) {
      rows.push(row);
    }
    removed = [];
    added = [];
  };
  for (const { op, tokens } of runs) {
    if (op === 'same') {
      flush();
      for (const text of tokens) rows.push({ kind: 'same', text });
    } else if (op === 'del') {
      removed = removed.concat(tokens);
    } else {
      added = added.concat(tokens);
    }
  }
  flush();
  return rows;
}
